import React, { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getFoodItem, createFoodItem, updateFoodItem } from '../../services/api';
import { useApiQuery, useApiMutation, useInvalidateQueries } from '../../hooks/useApiQuery';
import useToast from '../../hooks/useToast';
import FoodForm from '../../components/FoodForm';

const VendorFood = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const invalidate = useInvalidateQueries();
  
  const isEdit = Boolean(id);

  // ✅ React Query: Fetch single food item
  const {
    data: item,
    isLoading,
    isError,
    refetch: refetchItem,
  } = useApiQuery(
    ['food-item', id],
    () => getFoodItem(id).then(res => res.data),
    {
      enabled: isEdit,
      staleTime: 0, // ✅ Always refetch
      refetchOnMount: true,
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to load item');
        navigate('/vendor/food');
      },
    }
  );

  // ✅ React Query: Create food item mutation
  const createMutation = useApiMutation(
    (data) => createFoodItem(data).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess('Item added');
        invalidate(['food-items']);
        navigate('/vendor/food');
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to create item');
      },
    }
  );

  // ✅ React Query: Update food item mutation
  const updateMutation = useApiMutation(
    (data) => updateFoodItem(id, data).then(res => res.data),
    {
      onSuccess: () => {
        showSuccess('Item updated');
        invalidate(['food-items']);
        refetchItem(); // ✅ Refetch the detail
        navigate('/vendor/food');
      },
      onError: (error) => {
        showError(error.response?.data?.message || 'Failed to update item');
      },
    }
  );

  // ✅ Handle form submission
  const handleSubmit = useCallback((data) => {
    if (isEdit) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  }, [isEdit, createMutation, updateMutation]);

  // ✅ Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block w-10 h-10 border-2 border-[#1672b9] border-t-transparent rounded-full animate-spin mb-3"></div>
          <p className="text-gray-500 text-sm">Loading item...</p>
        </div>
      </div>
    );
  }

  // ✅ Error state
  if (isEdit && isError) {
    return (
      <div className="text-center py-10 text-[#708090]">
        <p className="text-red-500">Failed to load item</p>
        <button
          onClick={() => navigate('/vendor/food')}
          className="mt-4 text-[#1471b9] hover:underline"
        >
          Back to Food Items
        </button>
      </div>
    );
  }

  const isSubmitting = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="max-w-2xl mx-auto">
      <h1 className="text-2xl font-normal text-[#2b3445] mb-6">
        {isEdit ? 'Edit Food Item' : 'Add New Food Item'}
      </h1>
      <FoodForm
        initialData={item || null}  
        onSubmit={handleSubmit}
        onCancel={() => navigate('/vendor/food')}
        submitting={isSubmitting}
      />
    </div>
  );
};

export default VendorFood;